import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Tabs,
    Tab,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Typography,
    CircularProgress,
    TextField,
    TablePagination,
    InputAdornment,
    Button,
    FormControlLabel,
    Switch,
    Alert,
    Chip
} from '@mui/material';
import { Close, Download, Upload, CheckCircle, Search, Warning } from '@mui/icons-material';
import { alpha, styled } from '@mui/material/styles';
import Styles from './InviteUsersModal.module.scss';
// import { CSVLink } from "react-csv";
import axios from 'axios';
import ShareLinkComponent from './ShareLinkComponent';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs';


const InviteUsersModal = ({ open, onClose, uploadUrl, diyQuizId, assessment, setAssessments }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [file, setFile] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [invitedUsers, setInvitedUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false)
    const [error, setError] = useState(null);

    // Search and pagination states
    const [searchQuery, setSearchQuery] = useState('');
    const [pageNumber, setPageNumber] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(8);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // State for test/live mode in participants tab
    const [participantsMode, setParticipantsMode] = useState(0);// 0 for live, 1 for test
    
    const [selectedDate, setSelectedDate] = useState(assessment?.validity ? dayjs(assessment?.validity) : null);
    const [isAcceptingResponses, setIsAcceptingResponses] = useState(!!assessment?.is_active);
    const [isNotValid, setIsNotValid] = useState(false);

    const handleDateChange = (date) => {
        setSelectedDate(date)
    }

    useEffect(() => {
        setIsAcceptingResponses(!!assessment?.is_active)
        setParticipantsMode((assessment?.is_test_mode || assessment?.is_test_mode === 1) ? 1 : 0)
    }, [assessment]);

    useEffect(() => {
        const currentDate = new Date();
        setIsNotValid(!isAcceptingResponses || (assessment?.validity && currentDate > new Date(assessment.validity)));
    }, [assessment, isAcceptingResponses]);

    const handleSetValidity = async () => {
        setUploadLoading(true)
        try {
            const formattedDate = selectedDate.endOf('day').toISOString().slice(0, 23).replace('T', ' ');
            const response = await axios.patch(`/api/diy-quiz/${diyQuizId}/assessments/${assessment?.id}`, {
                validity: formattedDate
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 200 && response.data.data) {
                // Update assessments list with new validity
                setAssessments(prevAssessments =>
                    prevAssessments.map(item =>
                        item.id === assessment?.id ? { ...item, validity: formattedDate } : item
                    )
                );
            } else {
                setError('Error updating validity');
            }
        } catch (error) {
            console.error('Error updating validity:', error);
        } finally {
            setUploadLoading(false)
        }
    }

    const handleToggleResponses = async (event) => {
        const newCheckedState = event.target.checked;
        setIsAcceptingResponses(newCheckedState);
        const newStatus = !!newCheckedState ?
            assessment.completed ?
                'live' : 'published'
            : 'closed'
        setUploadLoading(true);

        try {
            const response = await axios.patch(`/api/diy-quiz/${diyQuizId}/assessments/${assessment?.id}`, {
                is_active: newCheckedState,
                status: newStatus
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 200 && response.data.data) {
                // Update assessments list with new is_active state
                setAssessments(prevAssessments =>
                    prevAssessments.map(item =>
                        item.id === assessment?.id ? { ...item, is_active: newCheckedState, status: newStatus } : item
                    )
                );
            } else {
                setError('Error updating validity');
                setIsAcceptingResponses(!newCheckedState); // Revert in case of error
            }
        } catch (error) {
            console.error('Error updating validity:', error);
            setIsAcceptingResponses(!newCheckedState); // Revert in case of error
        } finally {
            setUploadLoading(false);
        }
    };

    const fetchInvitedUsers = async (currentPage = 1, pageSize = rowsPerPage, search = searchQuery) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `/api/diy-quiz/${diyQuizId}/assessments/${assessment?.id}/invited-users`,
                {
                    params: {
                        pageNumber: currentPage,
                        pageSize,
                        search: search.trim(),
                        is_test_mode: participantsMode // New parameter for test/live mode
                    }
                }
            );

            const { data, pagination } = response.data;
            setInvitedUsers(data);
            setTotalItems(pagination.totalItems);
            setTotalPages(pagination.totalPages);
        } catch (error) {
            console.error('Error fetching invited users:', error);
            setError('Failed to load invited users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 1 && assessment?.id) {
            // When tab changes to invited users or mode changes, fetch first page
            fetchInvitedUsers(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, assessment?.id, diyQuizId, participantsMode]);

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleSearchClick = () => {
        fetchInvitedUsers(1, rowsPerPage, searchQuery);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        fetchInvitedUsers(1, rowsPerPage, '');
    };

    const handleChangePage = (event, newPage) => {
        setPageNumber(newPage);
        fetchInvitedUsers(newPage + 1, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPageNumber(0);
        fetchInvitedUsers(1, newRowsPerPage);
    };

    const handleDialogClose = () => {
        onClose();
        setFile(null);
        setUploadSuccess(false);
        setError(null);
        setSearchQuery('');
        setPageNumber(0);
        setActiveTab(0)
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (uploadSuccess) return; // Prevent further uploads
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile?.type === "text/csv") {
            setFile(droppedFile);
        }
    };

    const handleFileSelect = (e) => {
        if (uploadSuccess) return; // Prevent further uploads
        const selectedFile = e.target.files[0];
        if (selectedFile?.type === "text/csv") {
            setFile(selectedFile);
        }
    };

    const removeFile = () => {
        setFile(null)
    }

    const handleUpload = async () => {
        if (!file) return;
        setUploadLoading(true)

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('diy_quiz_id', diyQuizId); // Add DIY quiz ID
            formData.append('diy_quiz_assessment_id', assessment?.id);

            const response = await axios.post(uploadUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.status === 200) {
                console.log('File uploaded successfully');
                setFile(null);
                setUploadLoading(false)
                setUploadSuccess(true);
                if (activeTab === 1) {
                    fetchInvitedUsers(1);
                }
            }
        } catch (error) {
            console.error('Error uploading file:', error.response?.data || error.message);
        }
    };


    const formatDate = (utcTimestamp) => {
        if (!utcTimestamp) return '';
        const date = new Date(utcTimestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const formatStatus = (status) => {
        if (!status) return '';
        // Split by underscore and capitalize each word
        return status
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const GreenSwitch = styled(Switch)(({ theme }) => ({
        '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#008037',
            '&:hover': {
                backgroundColor: alpha('#008037', theme.palette.action.hoverOpacity),
            },
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#008037',
        },
    }));

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                className: Styles.modalPaper
            }}
        >
            <DialogTitle className={Styles.modalHeader}>
                <div className={Styles.titleContent}>
                    <h4>Invite Users - {assessment?.name}</h4>
                    <IconButton onClick={handleDialogClose} size="small">
                        <Close size={20} />
                    </IconButton>
                </div>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    className={Styles.tabs}
                    TabIndicatorProps={{ style: { backgroundColor: '#008037' } }}
                    sx={{
                        '& .MuiTab-root': {
                            color: 'gray',
                        },
                        '& .MuiTab-root.Mui-selected': {
                            color: '#008037',
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#008037',
                        },
                        '& .MuiTouchRipple-root': {
                            color: '#008037',
                        }
                    }}
                >
                    <Tab label="Share Link" />
                    <Tab label="Participants" />
                </Tabs>
            </DialogTitle>

            <DialogContent>
                {activeTab === 0 ? (
                    // <div className={Styles.csvSection}>
                    //     <div className={Styles.templateSection}>
                    //         <h3>Download Template</h3>
                    //         <p>Download our CSV template to ensure proper formatting of your user data.</p>
                    //         <CSVLink
                    //             data={[{ email: 'johndoe@example.com' }]}
                    //             headers={[{ label: 'email', key: 'email' }]}
                    //             filename="students.csv"
                    //         >
                    //             <button className={Styles.downloadButton}>
                    //                 <Download size={16} />
                    //                 <span>Download Template</span>
                    //             </button>
                    //         </CSVLink>
                    //     </div>
                    //     <div className={Styles.uploadSection}>
                    //         <h3>Upload CSV</h3>
                    //         {!uploadSuccess ? (
                    //             <div
                    //                 className={Styles.dropZone}
                    //                 onDragOver={handleDragOver}
                    //                 onDrop={handleDrop}
                    //             >
                    //                 <input
                    //                     type="file"
                    //                     accept=".csv"
                    //                     onChange={handleFileSelect}
                    //                     className={Styles.fileInput}
                    //                     id="csv-upload"
                    //                 />
                    //                 <Upload size={24} />
                    //                 <p>Drag and drop your CSV file here or</p>
                    //                 <label htmlFor="csv-upload" className={Styles.browseButton}>
                    //                     Browse Files
                    //                 </label>
                    //                 {file && (
                    //                     <div className={Styles.selectedFile}>
                    //                         <span>Selected: {file.name}</span>
                    //                         <IconButton size="small" onClick={removeFile}>
                    //                             <Close fontSize="inherit" />
                    //                         </IconButton>
                    //                     </div>
                    //                 )}
                    //             </div>
                    //         ) : (
                    //             <div className={Styles.successMessage}>
                    //                 <CheckCircle color="success" fontSize="large" />
                    //                 <Typography variant="h6">Invited Users Successfully!</Typography>
                    //             </div>
                    //         )}
                    //         {!uploadSuccess && (
                    //             <button
                    //                 className={`${Styles.uploadButton} ${!file ? Styles.disabled : ''}`}
                    //                 disabled={!file || uploadLoading}
                    //                 onClick={handleUpload}
                    //             >
                    //                 Upload and Send Invites
                    //             </button>
                    //         )}
                    //     </div>
                    // </div>
                    <div id="inviteContainer" className={Styles.inviteContainer}>
                        <div className={`${Styles.alertContainer} ${isNotValid ? Styles.show : Styles.hide}`}>
                            <Alert severity="warning">
                                This assessment is currently not accepting responses or its validity has expired.
                            </Alert>
                        </div>
                        <div id="validityContainer" className={Styles.validityContainer}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Validity"
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        disabled={!isAcceptingResponses}
                                        minDate={dayjs()}
                                        format="DD/MM/YYYY"
                                        sx={{
                                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#008037',
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: '#008037',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: '#008037',
                                            },
                                            // Calendar popup styles
                                            '& .MuiButtonBase-root-MuiPickersDay-root.Mui-selected': {
                                                backgroundColor: '#008037',  // Added !important
                                                '&:hover': {
                                                    backgroundColor: '#008037',
                                                },
                                                '&:focus': {
                                                    backgroundColor: '#008037',
                                                }
                                            },
                                            // Today's date highlight
                                            '& .MuiPickersDay-today': {
                                                borderColor: '#008037',
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                <Button
                                    variant="contained"
                                    onClick={handleSetValidity}
                                    disabled={!selectedDate || uploadLoading || !assessment.is_active || !isAcceptingResponses}
                                    sx={{ backgroundColor: '#008037' }}

                                >
                                    Set Validity
                                </Button>
                            </span>
                            <FormControlLabel
                                control={
                                    <GreenSwitch
                                        checked={isAcceptingResponses}
                                        onChange={handleToggleResponses}
                                    />
                                }
                                label="Accepting Responses"
                            />
                        </div>

                        <ShareLinkComponent shareLink={`${process.env.REACT_APP_URL}/assessments/${assessment?.id}`} />
                    </div>
                ) : (
                    <div className={Styles.invitedUsersSection}>
                        {loading ? (
                            <div className={Styles.loadingContainer}>
                                <CircularProgress size={40} style={{ color: '#008037' }} />
                                <Typography variant="body1" style={{ marginTop: '1rem' }}>
                                    Loading participants...
                                </Typography>
                            </div>
                        ) : error ? (
                            <div className={Styles.errorContainer}>
                                <Typography color="error" variant="body1">
                                    {error}
                                </Typography>
                                <button
                                    className={Styles.retryButton}
                                    onClick={fetchInvitedUsers}
                                >
                                    Retry
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className={Styles.participantsModeContainer}>
                                    <Paper elevation={1} className={Styles.participantsModeTabsPaper}>
                                        <Tabs
                                            value={participantsMode}
                                            onChange={(event, newValue) => {
                                                setParticipantsMode(newValue);
                                                setPageNumber(0);
                                                setSearchQuery('');
                                            }}
                                            className={Styles.participantsModeTab}
                                            TabIndicatorProps={{ style: { backgroundColor: '#008037' } }}
                                            sx={{
                                                '& .MuiTab-root': {
                                                    color: 'gray',
                                                    fontWeight: 500
                                                },
                                                '& .MuiTab-root.Mui-selected': {
                                                    color: '#008037',
                                                },
                                                '& .MuiTabs-indicator': {
                                                    backgroundColor: '#008037',
                                                },
                                            }}
                                            centered
                                        >
                                            <Tab label="Live Mode" />
                                            <Tab label="Test Mode" />
                                        </Tabs>
                                    </Paper>
                                </div>
                                <div className={Styles.tableControls}>
                                    <TextField
                                        placeholder="Search users..."
                                        variant="outlined"
                                        size="small"
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        className={Styles.searchField}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <IconButton size="small" onClick={handleSearchClick}>
                                                        <Search />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {searchQuery && (
                                                        <IconButton size="small" onClick={handleClearSearch}>
                                                            <Close />
                                                        </IconButton>
                                                    )}
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </div>
                                <div id='tableContainer' className={Styles.tableContainer}>
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Email</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Sent Date</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {invitedUsers.length > 0 ? (
                                                    invitedUsers.map((user, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{user.email}</TableCell>
                                                            <TableCell>
                                                                <span className={`${Styles.status} ${Styles[user.status.toLowerCase()]}`}>
                                                                    {formatStatus(user.status)}
                                                                </span>
                                                            </TableCell>
                                                            <TableCell>{formatDate(user.created_at)}</TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={3} align="center">
                                                            <Typography variant="body1" color="textSecondary">
                                                                No users found
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <TablePagination
                                        component="div"
                                        count={totalItems}
                                        page={pageNumber}
                                        onPageChange={handleChangePage}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[8, 16, 24, 32]}
                                        className={Styles.tablePagination}
                                    />
                                    {loading && invitedUsers.length > 0 && (
                                        <div className={Styles.tableOverlayLoader}>
                                            <CircularProgress size={24} style={{ color: '#008037' }} />
                                        </div>
                                    )}
                                </div>

                            </>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog >
    );
};

export default InviteUsersModal;